import { useEffect, useState, type FormEvent } from 'react';
import { Bot, Download, Save, Settings2 } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { AdminSettings, AiModerationPendingItem, AiModerationSettings, AiModerationTestResult } from '../types';
import { centsToInput, currencyInputToCents, formatTime } from '../utils';
import { Badge, Button, EmptyBlock, Field, LoadingBlock, PageHeader, Panel, Select, Textarea, TextInput } from '../components/ui';

export function SettingsPage() {
  const { settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteMeta, setInviteMeta] = useState<AdminSettings | null>(null);
  const [feeRecipientUserId, setFeeRecipientUserId] = useState('');
  const [aiSettings, setAiSettings] = useState<AiModerationSettings | null>(null);
  const [aiKey, setAiKey] = useState('');
  const [aiClearKey, setAiClearKey] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPending, setAiPending] = useState<AiModerationPendingItem[]>([]);
  const [aiTestResult, setAiTestResult] = useState<AiModerationTestResult | null>(null);

  useEffect(() => setForm(settings), [settings]);
  useEffect(() => { if (inviteMeta?.tradeFeeRecipientUserId) setFeeRecipientUserId(inviteMeta.tradeFeeRecipientUserId); }, [inviteMeta?.tradeFeeRecipientUserId]);
  const loadAiModeration = async () => {
    if (!permissions.isSuperadmin) return;
    const [settingsData, pendingData] = await Promise.all([
      api<{ settings: AiModerationSettings }>('/api/admin/ai-moderation/settings'),
      api<{ items: AiModerationPendingItem[] }>('/api/admin/ai-moderation/pending')
    ]);
    setAiSettings(settingsData.settings);
    setAiPending(pendingData.items);
  };

  useEffect(() => {
    if (!permissions.canEditSettings) return;
    void api<{ settings: AdminSettings }>('/api/admin/settings').then(data => setInviteMeta(data.settings)).catch(() => setInviteMeta(null));
  }, [permissions.canEditSettings]);

  useEffect(() => {
    if (!permissions.isSuperadmin) return;
    void loadAiModeration().catch(error => toast(error instanceof ApiError ? error.message : 'AI审核设置加载失败', 'error'));
  }, [permissions.isSuperadmin]);

  if (!permissions.canEditSettings) return <EmptyBlock title="无权访问" description="系统设置仅对管理员开放。" />;

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm(current => ({ ...current, [key]: value }));
  const updateAi = <K extends keyof AiModerationSettings>(key: K, value: AiModerationSettings[K]) => setAiSettings(current => current ? { ...current, [key]: value } : current);

  const aiPayload = () => aiSettings ? ({
    enabled: aiSettings.enabled,
    prompt: aiSettings.prompt,
    apiKey: aiKey || undefined,
    clearApiKey: aiClearKey
  }) : null;

  const persistAiSettings = async () => {
    const payload = aiPayload();
    if (!payload) throw new Error('AI审核设置尚未加载');
    await api('/api/admin/ai-moderation/settings', { method: 'PATCH', body: JSON.stringify(payload) });
    const latest = await api<{ settings: AiModerationSettings }>('/api/admin/ai-moderation/settings');
    setAiSettings(latest.settings);
    setAiKey('');
    setAiClearKey(false);
    return latest.settings;
  };

  const saveAiSettings = async (event: FormEvent) => {
    event.preventDefault();
    setAiBusy(true);
    try {
      await persistAiSettings();
      const pendingData = await api<{ items: AiModerationPendingItem[] }>('/api/admin/ai-moderation/pending');
      setAiPending(pendingData.items);
      toast('AI审核设置已保存', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'AI审核设置保存失败', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  const testAiSettings = async () => {
    setAiBusy(true);
    try {
      await persistAiSettings();
      const data = await api<{ result: AiModerationTestResult }>('/api/admin/ai-moderation/test', { method: 'POST', body: JSON.stringify({}) });
      setAiTestResult(data.result);
      const label = data.result.decision === 'allow' ? '允许' : data.result.decision === 'reject' ? '拒绝' : '建议人工复核';
      toast(`API 测试完成：${label}，风险分 ${data.result.riskScore}`, data.result.decision === 'allow' ? 'success' : 'info');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'API 测试失败', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  const reviewAiItem = async (item: AiModerationPendingItem, action: 'approve' | 'reject') => {
    try {
      await api(`/api/admin/ai-moderation/pending/${item.kind}/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action })
      });
      setAiPending(current => current.filter(entry => !(entry.kind === item.kind && entry.id === item.id)));
      toast(action === 'approve' ? '内容已通过并公开' : '内容已拒绝', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '审核操作失败', 'error');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          siteName: form.siteName,
          currency: form.currency,
          initBalanceCents: currencyInputToCents(String(form.initBalanceCents / 100)),
          registrationEnabled: form.registrationEnabled,
          registrationInviteCode: inviteCode || undefined,
          registrationNotice: form.registrationNotice,
          commissionPolicy: form.commissionPolicy,
          marketPolicy: form.marketPolicy,
          auctionPolicy: form.auctionPolicy,
          tradeFeePercent: Number(form.tradeFeePercent) || 0,
          tradeFeeRecipientUserId: feeRecipientUserId || null,
          allowUnboundBoardMessages: form.allowUnboundBoardMessages,
          allowUnboundBoardComments: form.allowUnboundBoardComments,
          allowUnboundBoardLikes: form.allowUnboundBoardLikes
        })
      });
      setInviteCode('');
      const latest = await api<{ settings: AdminSettings }>('/api/admin/settings');
      setInviteMeta(latest.settings);
      await refresh();
      toast('系统设置已保存', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '保存失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="系统设置" description="管理站点名称、货币、注册开关和业务说明。" />
      <div className="two-column wide-left">
        <Panel title="基础设置" eyebrow="SYSTEM">
          <form className="stack-form" onSubmit={submit}>
            <div className="form-grid">
              <Field label="站点名称"><TextInput value={form.siteName} onChange={event => update('siteName', event.target.value)} maxLength={40} required /></Field>
              <Field label="虚拟货币名称"><TextInput value={form.currency} onChange={event => update('currency', event.target.value)} maxLength={12} required /></Field>
            </div>
            <Field label="物品交易手续费（%）" hint="市场成交和拍卖成交时从卖家收入中扣除，归入最早创建的活跃超级管理员账号；可随时修改。"><TextInput type="number" min="0" max="100" step="0.1" value={form.tradeFeePercent} onChange={event => update('tradeFeePercent', Number(event.target.value))} required /></Field>
            <Field label="手续费接收账号" hint="只能选择当前活跃的超级管理员"><Select value={feeRecipientUserId} onChange={event => setFeeRecipientUserId(event.target.value)}>{inviteMeta?.superadmins.map(account => <option key={account.id} value={account.id}>{account.displayName}（@{account.username}）</option>)}</Select></Field>
            <Field label="新成员初始资金"><TextInput type="number" min="0" step="0.01" value={centsToInput(form.initBalanceCents)} onChange={event => update('initBalanceCents', currencyInputToCents(event.target.value))} /></Field>
            <label className="check-line"><input type="checkbox" checked={form.registrationEnabled} onChange={event => update('registrationEnabled', event.target.checked)} /> 允许新成员自助注册</label>
            <Field label="更换注册邀请码" hint="仅在需要轮换时填写；邀请码只保存哈希，保存后无法找回原文">
              <TextInput value={inviteCode} onChange={event => setInviteCode(event.target.value.toUpperCase())} minLength={8} maxLength={64} pattern="[A-Za-z0-9_-]+" placeholder="输入新的邀请码" autoComplete="off" />
            </Field>
            <p className="form-note">当前邀请码状态：{inviteMeta?.registrationInviteConfigured ? '已设置，前缀 ' + (inviteMeta.registrationInvitePrefix || '****') + '****' : '未设置'}</p>
            <Field label="注册说明"><Textarea value={form.registrationNotice} onChange={event => update('registrationNotice', event.target.value)} rows={2} maxLength={500} /></Field>
            <div className="form-note"><strong>留言板权限</strong><br />以下开关只允许“已登录但未绑定游戏角色”的账号操作；关闭时仍要求绑定游戏角色。游客未登录时始终不能留言、评论或点赞。</div>
            <label className="check-line"><input type="checkbox" checked={form.allowUnboundBoardMessages} onChange={event => update('allowUnboundBoardMessages', event.target.checked)} /> 允许未绑定账号发布留言</label>
            <label className="check-line"><input type="checkbox" checked={form.allowUnboundBoardComments} onChange={event => update('allowUnboundBoardComments', event.target.checked)} /> 允许未绑定账号评论留言</label>
            <label className="check-line"><input type="checkbox" checked={form.allowUnboundBoardLikes} onChange={event => update('allowUnboundBoardLikes', event.target.checked)} /> 允许未绑定账号点赞留言</label>
            <Field label="委托规则说明"><Textarea value={form.commissionPolicy} onChange={event => update('commissionPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Field label="市场规则说明"><Textarea value={form.marketPolicy} onChange={event => update('marketPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Field label="拍卖规则说明"><Textarea value={form.auctionPolicy} onChange={event => update('auctionPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Button variant="primary" type="submit" loading={busy}><Save size={17} /> 保存设置</Button>
          </form>
        </Panel>
        <div className="side-stack">
          <Panel title="权限边界" eyebrow="SECURITY">
            <div className="policy-list">
              <p><Settings2 size={17} /><span><strong>成员</strong>只能委托、购买、拍卖和兑换。</span></p>
              <p><Settings2 size={17} /><span><strong>管理员</strong>可管理普通成员、公告、兑换码和系统设置。</span></p>
              <p><Settings2 size={17} /><span><strong>超级管理员</strong>可管理管理员、任命与撤销管理员。</span></p>
            </div>
          </Panel>
          {permissions.canExportBackup && (
            <Panel title="完整备份" eyebrow="BACKUP">
              <p className="muted">导出包含全部业务表和审计记录。文件含敏感账号数据，请妥善保管。</p>
              <Button variant="danger" className="wide" onClick={() => void (async () => {
                const approved = await confirm({ title: '确认导出完整备份？', body: '导出文件包含所有账号数据、业务数据和审计记录，请妥善保管。', danger: true, confirmText: '确认导出' });
                if (approved) window.location.href = '/api/admin/backup';
              })()}><Download size={17} /> 导出 JSON 备份</Button>
            </Panel>
          )}
        </div>
      </div>

      {permissions.isSuperadmin && (
        <Panel title="AI 留言审核" eyebrow="DEEPSEEK V4 FLASH" className="top-gap">
          {!aiSettings ? <LoadingBlock label="正在加载 AI 审核设置" /> : (
            <div className="ai-settings-layout">
              <form className="stack-form" onSubmit={saveAiSettings}>
                <div className="form-note"><Bot size={17} /><span>AI 审核只处理普通成员的留言和评论；管理员发布内容会直接通过。API Key 仅加密保存在后端。</span></div>
                <div className="ai-model-note"><Badge tone="active">{aiSettings.modelLabel}</Badge><span>固定模型：{aiSettings.model} · 低风险自动公开，中风险进入复核，高风险自动拒绝</span></div>
                <label className="check-line"><input type="checkbox" checked={aiSettings.enabled} onChange={event => updateAi('enabled', event.target.checked)} /> 启用 AI 自动审核</label>
                <Field label="API Key" hint={aiSettings.apiKeyConfigured ? `已配置，末四位 ${aiSettings.apiKeyHint || '****'}；留空不会修改现有密钥` : '只需填写 API Key，接口地址和模型已固定'}>
                  <TextInput type="password" value={aiKey} onChange={event => { setAiKey(event.target.value); setAiClearKey(false); }} placeholder={aiSettings.apiKeyConfigured ? '留空保持现有密钥' : '输入 DeepSeek API Key'} maxLength={400} autoComplete="new-password" disabled={aiClearKey} />
                </Field>
                {aiSettings.apiKeyConfigured && <label className="check-line"><input type="checkbox" checked={aiClearKey} onChange={event => setAiClearKey(event.target.checked)} /> 删除已保存的 API Key</label>}
                <details className="ai-prompt-details">
                  <summary>审核规则与提示词（可选）</summary>
                  <Field label="审核提示词" hint="默认内容已经适合留言板；只有需要调整审核重点时再修改"><Textarea rows={7} maxLength={6000} value={aiSettings.prompt} onChange={event => updateAi('prompt', event.target.value)} required /></Field>
                </details>
                <div className="inline-actions">
                  <Button variant="primary" type="submit" loading={aiBusy}><Save size={16} /> 保存 AI 设置</Button>
                  <Button type="button" onClick={() => void testAiSettings()} disabled={aiBusy}><Bot size={16} /> 测试 API</Button>
                </div>
                {aiTestResult && (
                  <div className={'ai-test-result ' + aiTestResult.decision}>
                    <div><strong>测试结果</strong><Badge tone={aiTestResult.decision === 'allow' ? 'good' : aiTestResult.decision === 'reject' ? 'danger' : 'warn'}>{aiTestResult.decision === 'allow' ? '允许' : aiTestResult.decision === 'reject' ? '拒绝' : '人工复核'}</Badge></div>
                    <p>风险分 {aiTestResult.riskScore} · 耗时 {aiTestResult.latencyMs}ms · 模型 {aiTestResult.model || '—'}</p>
                    <p>{aiTestResult.reason}</p>
                    {aiTestResult.categories.length > 0 && <small>分类：{aiTestResult.categories.join('、')}</small>}
                  </div>
                )}
              </form>

              <section className="ai-review-queue">
                <header><div><span className="eyebrow">REVIEW QUEUE</span><h3>待人工复核 · {aiPending.length}</h3></div><Button type="button" onClick={() => void loadAiModeration()} disabled={aiBusy}>刷新</Button></header>
                {aiPending.length === 0 ? <p className="muted">当前没有待复核内容。</p> : <div className="ai-review-list">{aiPending.map(item => (
                  <article className="ai-review-item" key={item.kind + ':' + item.id}>
                    <header><div><strong>{item.kind === 'message' ? '留言' : '评论'} · {item.displayName}</strong><small>@{item.username} · {formatTime(item.createdAt)}</small></div><Badge tone="warn">风险 {item.moderationScore ?? '—'}</Badge></header>
                    <p>{item.content}</p>
                    <small>{item.moderationReason || '等待人工复核'}{item.moderationCategories.length > 0 ? ' · ' + item.moderationCategories.join('、') : ''}</small>
                    <div className="inline-actions"><Button variant="primary" type="button" onClick={() => void reviewAiItem(item, 'approve')}>通过并公开</Button><Button variant="danger" type="button" onClick={() => void reviewAiItem(item, 'reject')}>拒绝</Button></div>
                  </article>
                ))}</div>}
              </section>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}








